<template>
  <div class="p-5 mb-5">
    <div class="flex-grow-1 d-flex flex-column justify-content-center">
      <div class="flex-grow-0 flex-shrink-0 container">
        <div class="row justify-content-md-center">
          <div class="col col-lg-8">
            <div class="bg-white shadow rounded-4">
              <div class="p-5 text-center">
                <h1 class="fw-bold fs-2">Enter Sign-In Code</h1>
                <p class="lead">
                  We've sent a 6 character code to
                  <a :href="'mailto:' + email">{{ email }}</a
                  >. The code expires shortly, so please enter it soon.
                </p>

                <form @submit.prevent="onSubmit">
                  <div class="card-body">
                    <div class="form-floating mb-3">
                      <Field
                        name="code"
                        type="text"
                        v-slot="{ field, errorMessage, meta }"
                      >
                        <input
                          v-bind="field"
                          type="text"
                          class="form-control rounded-3"
                          :class="{
                            'is-invalid': meta.touched && errorMessage
                          }"
                          id="code"
                          :disabled="isLoading"
                          placeholder="Code"
                          pattern="[A-Za-z0-9]*"
                          inputmode="text"
                          autocomplete="one-time-code"
                        />
                        <div
                          class="invalid-feedback"
                          v-if="meta.touched && errorMessage"
                        >
                          {{ errorMessage }}
                        </div>
                      </Field>
                      <label for="code">Code</label>
                    </div>
                  </div>

                  <div class="card-footer">
                    <button
                      type="submit"
                      class="btn btn-lg btn-primary w-100"
                      :disabled="isLoading || Object.keys(errors).length > 0"
                    >
                      <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      {{ isLoading ? 'Verifying...' : 'Sign In' }}
                    </button>
                  </div>
                </form>

                <div v-if="error" class="alert alert-danger mt-3" role="alert">
                  {{ error }}
                </div>

                <div class="mt-4 text-center">
                  <p class="text-muted mb-3">
                    Can't find your code? Check your spam folder!
                  </p>
                  <div class="d-flex justify-content-center gap-5">
                    <a
                      href="https://mail.google.com/mail/u/0/#search/ComplianceClerk"
                      target="_blank"
                      class="text-muted text-decoration-none small"
                      rel="noopener noreferrer"
                    >
                      <span class="d-inline-block align-middle me-1">
                        <img :src="gmail" alt="Gmail" class="icon" />
                      </span>
                      <span class="align-middle">Open Gmail</span>
                    </a>
                    <a
                      href="https://outlook.office.com/mail/search/ComplianceClerk"
                      target="_blank"
                      class="text-muted text-decoration-none small"
                      rel="noopener noreferrer"
                    >
                      <span class="d-inline-block align-middle me-1">
                        <img :src="outlook" alt="Outlook" class="icon" />
                      </span>
                      <span class="align-middle">Open Outlook</span>
                    </a>
                  </div>
                  <div class="text-center mt-3">
                    <button
                      type="button"
                      class="btn btn-link"
                      @click="handleResendCode"
                      :disabled="isResending || !canResend()"
                    >
                      {{
                        isResending
                          ? 'Sending...'
                          : remainingResendTime > 0
                            ? `Resend Code (${remainingResendTime}s)`
                            : 'Resend Code'
                      }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useForm, Field } from 'vee-validate'
import * as yup from 'yup'
import gmail from '@static/svg/brands/gmail.svg'
import outlook from '@static/svg/brands/outlook.svg'

const props = defineProps({
  email: {
    type: String,
    required: true
  }
})

const userStore = useUserStore()

const isLoading = ref(false)
const isResending = ref(false)
const error = ref('')

// Rate limiting state
const lastSubmitTime = ref(0)
const lastResendTime = ref(0)
const submitCooldown = 2000 // 2 seconds between submit attempts
const resendCooldown = 30000 // 30 seconds between resend attempts

const remainingResendTime = ref(0)
const resendTimer = ref(null)

const emit = defineEmits(['code-confirmed'])

const schema = yup.object({
  code: yup
    .string()
    .required('Code is required')
    .matches(/^[A-Za-z0-9]+$/, 'Code can only contain letters and numbers')
    .min(6, 'Code must be 6 characters')
    .max(6, 'Code must be 6 characters')
    .trim()
})

const { handleSubmit, errors, setFieldError } = useForm({
  validationSchema: schema,
  initialValues: {
    code: ''
  }
})

function canSubmit() {
  const now = Date.now()
  if (now - lastSubmitTime.value < submitCooldown) {
    setFieldError('code', 'Please wait a moment before trying again')
    return false
  }
  return true
}

function canResend() {
  const now = Date.now()
  if (now - lastResendTime.value < resendCooldown) {
    return false
  }
  return true
}

function updateResendTimer() {
  if (resendTimer.value) {
    clearInterval(resendTimer.value)
  }

  const updateTimer = () => {
    const now = Date.now()
    const timeLeft = Math.max(0, resendCooldown - (now - lastResendTime.value))
    remainingResendTime.value = Math.ceil(timeLeft / 1000)

    if (timeLeft === 0 && resendTimer.value) {
      clearInterval(resendTimer.value)
      resendTimer.value = null
    }
  }

  updateTimer()
  resendTimer.value = setInterval(updateTimer, 1000)
}

const onSubmit = handleSubmit(async (values) => {
  if (isLoading.value || !canSubmit()) return

  error.value = ''
  isLoading.value = true
  lastSubmitTime.value = Date.now()

  try {
    const success = await userStore.confirmLoginCode(values.code)

    if (success) {
      emit('code-confirmed')
    } else {
      error.value = 'Invalid code. Please try again.'
    }
  } catch (e) {
    error.value = 'An unexpected error occurred. Please try again.'
    console.error('Login code confirmation error:', e)
  } finally {
    isLoading.value = false
  }
})

async function handleResendCode() {
  if (isResending.value || !canResend()) return

  error.value = ''
  isResending.value = true
  lastResendTime.value = Date.now()
  updateResendTimer()

  try {
    await userStore.requestLoginCode(props.email)
  } catch (e) {
    error.value = 'Failed to resend code. Please try again.'
    console.error('Resend code error:', e)
  } finally {
    isResending.value = false
  }
}

onUnmounted(() => {
  if (resendTimer.value) {
    clearInterval(resendTimer.value)
    resendTimer.value = null
  }
})
</script>
